/**
 * More — consolidated parent app page: Pickup, Absences, Photos, Documents,
 * Health, Gallery, Contact center, Settings, Notification tone.
 *
 * Iteration 29 — i18n-aware. All tile labels + section eyebrows route through
 * useI18n().
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useChild } from "../lib/childContext";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import Brand from "../components/Brand";
import ManageMyData from "../components/ManageMyData";
import ParentBenefitsRail from "../components/ParentBenefitsRail";
import PushToggle from "../components/PushToggle";
import TwoFASetup from "../components/TwoFASetup";
import {
  Shield,
  BedDouble,
  Sparkles,
  FileText,
  Phone,
  Settings as SettingsIcon,
  ChevronRight,
  Users,
  ArrowLeft,
  BellOff,
  Bell,
  Pill,
  ImageIcon,
  CreditCard,
} from "lucide-react";

function buildTiles(t) {
  return [
    {
      href: "/trust",
      icon: Users,
      label: t("more.tile.pickup"),
      blurb: t("more.tile.pickup.blurb"),
      testid: "more-pickup-people",
      iconBg: "var(--pastel-mint)",
      iconFg: "#1F8A3F",
    },
    {
      href: "/home",
      icon: BedDouble,
      label: t("more.tile.absences"),
      blurb: t("more.tile.absences.blurb"),
      testid: "more-absences",
      iconBg: "var(--pastel-sky)",
      iconFg: "#2563EB",
    },
    {
      href: "/moments",
      icon: Sparkles,
      label: t("more.tile.moments"),
      blurb: t("more.tile.moments.blurb"),
      testid: "more-moments",
      iconBg: "var(--pastel-yellow)",
      iconFg: "#B45309",
    },
    {
      href: "/more/gallery",
      icon: ImageIcon,
      label: t("more.tile.gallery"),
      blurb: t("more.tile.gallery.blurb"),
      testid: "more-gallery",
      iconBg: "var(--pastel-peach)",
      iconFg: "#C2410C",
    },
    {
      href: "/more/health",
      icon: Pill,
      label: t("more.tile.health"),
      blurb: t("more.tile.health.blurb"),
      testid: "more-health",
      iconBg: "var(--pastel-pink)",
      iconFg: "#BE185D",
    },
    {
      href: "/notifications",
      icon: Bell,
      label: t("more.tile.notifications"),
      blurb: t("more.tile.notifications.blurb"),
      testid: "more-notifications",
      iconBg: "var(--pastel-lavender)",
      iconFg: "#1F3A2A",
    },
    {
      href: "/more/documents",
      icon: FileText,
      label: t("more.tile.documents"),
      blurb: t("more.tile.documents.blurb"),
      testid: "more-documents",
      iconBg: "var(--color-primary-soft)",
      iconFg: "#1F3A2A",
    },
    {
      href: "/billing",
      icon: CreditCard,
      label: t("billing.nav.billing"),
      blurb: t("billing.nav.billing_blurb"),
      testid: "more-billing",
      iconBg: "var(--color-primary-soft)",
      iconFg: "#1F3A2A",
    },
    {
      href: "/trust",
      icon: Shield,
      label: t("more.tile.trust"),
      blurb: t("more.tile.trust.blurb"),
      testid: "more-trust",
      iconBg: "#1A1B2E",
      iconFg: "#FFFFFF",
    },
    {
      href: "/settings",
      icon: SettingsIcon,
      label: t("more.tile.settings"),
      blurb: t("more.tile.settings.blurb"),
      testid: "more-settings",
      iconBg: "var(--color-bg-muted)",
      iconFg: "#6B7280",
    },
  ];
}

export default function More() {
  const { user, signOut } = useAuth();
  const { selected } = useChild();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [tone, setTone] = useState(user?.notification_tone || "calm");
  const [contact, setContact] = useState(null);

  useEffect(() => {
    setTone(user?.notification_tone || "calm");
  }, [user?.notification_tone]);

  useEffect(() => {
    if (!selected?.workspace_id) {
      setContact(null);
      return;
    }
    let alive = true;
    api
      .get(`/workspaces/${selected.workspace_id}`)
      .then((r) => {
        if (alive) setContact(r.data?.workspace || null);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [selected?.workspace_id]);

  const tiles = buildTiles(t);

  return (
    <div data-testid="more-screen" className="min-h-screen">
      <header className="px-5 sm:px-10 pt-6 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate("/home")}
          data-testid="more-back"
          aria-label={t("common.back")}
          className="flex items-center gap-2 text-[#6A6863] hover:text-[#2C2B29] transition-colors"
        >
          <ArrowLeft size={18} strokeWidth={1.5} />
          <span className="text-sm">Home</span>
        </button>
        <Brand size="sm" dataTestId="brand-more" />
      </header>

      <main className="px-6 sm:px-10 pt-8 pb-32 max-w-5xl mx-auto fade-up lg:flex lg:gap-10 lg:items-start">
        <div className="flex-1 min-w-0 max-w-2xl">
        <span className="eyebrow">More</span>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[#1A1B2E]">
          {t("more.title.line1")}
          <br />
          <span className="text-[#1F3A2A] italic font-light">{t("more.title.line2")}</span>
        </h1>

        <ul
          data-testid="more-tiles"
          className="mt-10 rounded-3xl bg-white border hairline overflow-hidden divide-y divide-[#171717]/6"
        >
          {tiles.map((tile) => (
            <li key={tile.testid}>
              <Link
                to={tile.href}
                data-testid={tile.testid}
                className="flex items-center gap-5 px-5 py-4 hover:bg-[#F2EFEA]/70 transition-colors duration-300"
              >
                <span
                  className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0"
                  style={{ background: tile.iconBg }}
                  aria-hidden
                >
                  <tile.icon
                    size={20}
                    strokeWidth={1.6}
                    style={{ color: tile.iconFg }}
                  />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-base text-[#171717]">
                    {tile.label}
                  </div>
                  <div className="text-sm text-[#525252] mt-0.5">{tile.blurb}</div>
                </div>
                <ChevronRight
                  size={18}
                  strokeWidth={1.6}
                  className="text-[#A3A3A3] shrink-0"
                />
              </Link>
            </li>
          ))}
        </ul>

        {/* Tone preview */}
        <section className="mt-12 rounded-3xl border hairline bg-[#F2EFEA]/60 p-7">
          <div className="flex items-center gap-2">
            <BellOff size={14} strokeWidth={1.5} className="text-[#8A9984]" />
            <span className="eyebrow">{t("more.tone.eyebrow")}</span>
          </div>
          <p
            data-testid="more-tone-current"
            className="mt-3 text-[#1A1B2E] text-base font-medium capitalize"
          >
            {tone.replace("_", " ")}
          </p>
          <Link
            to="/settings"
            data-testid="more-change-tone"
            className="mt-4 inline-block text-sm text-[#1F3A2A] underline underline-offset-4 font-medium"
          >
            {t("more.change_tone")}
          </Link>
        </section>

        {contact && (
          <section className="mt-6 rounded-3xl border hairline bg-white p-6 flex items-start gap-4">
            <span
              className="h-10 w-10 rounded-full flex items-center justify-center shrink-0"
              style={{ background: "#1F3A2A" }}
              aria-hidden
            >
              <Phone size={16} strokeWidth={1.5} className="text-white" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="eyebrow">{t("more.contact")}</div>
              <div className="mt-2 font-serif text-lg text-[#2C2B29]">
                {contact.name}
              </div>
              <div className="mt-2 text-sm text-[#6A6863]">
                Reach out softly via /messages, or call the front desk in person.
              </div>
            </div>
          </section>
        )}

        <section className="mt-10">
          <button
            type="button"
            onClick={() => {
              signOut();
              navigate("/");
            }}
            data-testid="more-signout"
            className="btn-ghost"
          >
            {t("more.signout")}
          </button>
        </section>

        <section className="mt-12">
          <span className="eyebrow">{t("more.notifications.eyebrow")}</span>
          <div className="mt-3 rounded-3xl bg-white border hairline p-5">
            <PushToggle testid="more-push-toggle" />
          </div>
        </section>

        <section className="mt-8">
          <span className="eyebrow">{t("more.sign_in_safety.eyebrow")}</span>
          <div className="mt-3 rounded-3xl bg-white border hairline p-5">
            <TwoFASetup testid="more-twofa" />
          </div>
        </section>

        <section className="mt-8">
          <ManageMyData testid="more-manage-data" />
        </section>
        </div>
        {user?.role === "parent" && <ParentBenefitsRail />}
      </main>
    </div>
  );
}
